

import React, { useEffect, useState, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Box, ThemeProvider, CircularProgress, Typography } from "./utils/mui-imports";

// Critical imports only - these are needed for initial render
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { trackGuestCredits, getGuestId, checkGuestInMySQL } from "./utils/guest";
import { initTracking, trackPageView } from './utils/tracking';
import { theme } from "./theme";


// Import critical components directly (needed for main functionality)
//import Layout from "./components/archived/Layout";
//import SwiperCarousel from "./components/SwiperCarousel";
// Login now lazy loaded below

//import MobileLayout from "./components/MobileLayoutV3";
//import MobileLayoutFastUI from "./components/MobileLayoutFastUI";
// Import critical components directly (needed for main functionality)
// Import critical pages directly for better performance

//import MobileLayoutNewUI from "./components/MobileLayoutNewUI";
//import PinterestCollagePage from "./pages/PinterestCollagePage";
import PinterestStylePinPage from "./pages/PinterestStylePinPage";

import MobileLayoutSlimUI from "./components/MobileLayoutSlimUI";
import CurateLookPage from "./pages/CurateLookPage";


// All pages lazy loaded for maximum bundle size optimization
const Navbar = React.lazy(() => import("./components/navbarNew"));
const PinterestRemixLookPageNewUI = React.lazy(() => import("./pages/PinterestRemixLookPageNewUI"));
const Footer = React.lazy(() => import("./components/FooterNewUI"));
const Login = React.lazy(() => import("./components/Login"));

// Non-critical components - Lazy loaded (1.1MB savings)
const AuthPage = React.lazy(() => import("./pages/AuthPageNew"));
const MyProfile = React.lazy(() => import("./pages/MyProfileNewUI"));
//const ProfileOld = React.lazy(() => import("./pages/MyProfile").then(module => ({ default: module.MyProfile })));
const ShopTheLook = React.lazy(() => import("./components/ShopTheLookNewUI"));
const ShopThePin = React.lazy(() => import("./components/ShopThePin"));
const ShopTheAvatar = React.lazy(() => import("./components/ShopTheAvatar"));
const ShopThePostNewUI = React.lazy(() => import("./components/ShopThePostNewUI"));
const LookBook = React.lazy(() => import("./components/LookBookNewUI"));
const ShowcaseLookPage = React.lazy(() => import("./pages/ShowcaseLookPageNewUI"));
const ShowcaseAvatarPage = React.lazy(() => import("./pages/ShowcaseAvatarPage"));
const ShowcasePostPage = React.lazy(() => import("./pages/ShowcasePostPage"));
const BrowseLooksPage = React.lazy(() => import("./pages/BrowseLooksPage"));
const TermsPage = React.lazy(() => import("./pages/TermsPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const RemixLookPage = React.lazy(() => import("./pages/RemixLookPageNewUI"));
const PinterestOAuth = React.lazy(() => import("./components/PinterestOAuth"));


function RouteChangeTracker() {
  const location = useLocation();

  useEffect(() => {
    console.log('Route changed to:', location.pathname + location.search);
    trackPageView(location.pathname + location.search, true); // Skip CAPI for route changes
  }, [location]);

  return null;
}

function App() {
  const trialCount = parseInt(localStorage.getItem("trialCount")) || 3;
  const [guestId, setGuestId] = useState(null)
  
  useEffect(() => {
    // Defer tracking initialization to avoid blocking LCP
    setTimeout(() => {
      initTracking();
    }, 0);
    
    // Defer guest initialization to avoid blocking LCP
    const initializeGuest = async () => {
      try {
        const gid = await getGuestId();
        setGuestId(gid)
        console.log("Guest ID in App.js:", gid);
        await trackGuestCredits();
      } catch (error) {
        console.error("Guest initialization error:", error);
      }
    };

    // Run guest initialization in background after critical rendering
    setTimeout(initializeGuest, 0);
    
    // ✅ Attach guest functions to window for debugging
    //window.checkGuestInFirestore = checkGuestInFirestore;
    //window.checkGuestInMySQL = checkGuestInMySQL;
    //window.getGuestId = getGuestId;
    //window.trackGuestCredits = trackGuestCredits;

  }, []);

  return (
    <div>
      <AuthProvider>
        <Router>
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60px', borderBottom: '1px solid #eee' }}>
              <CircularProgress size={24} />
            </Box>
          }>
            <Navbar guestID={guestId} />
          </Suspense>

          <ThemeProvider theme={theme}>
            <Box
              width={"100%"}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignContent={"center"}
              alignItems={"center"}
            >
              <Box
                component="div"
                sx={{
                  // hide on xs+sm, show on md+
                  display: { xl: "block" },
                  maxWidth: 444,
                  width: "100%",
                }}
              >
                <RouteChangeTracker /> {/* ✅ Inline here, no wrapper needed */}
                <Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                  </Box>
                }>
                  <Routes>
                  <Route path="/" element={<MobileLayoutSlimUI />} />
                  <Route path="/signup" element={<AuthPage />} />
                  {/* ✅ CurateLookPage now directly loaded */}
                  <Route path="/curate-look" element={<CurateLookPage />} />
                  
                  <Route element={<ProtectedRoute />}>
                    <Route path="/shop-the-avatar" element={<ShopTheAvatar/>} />
                    <Route path="/profile" element={<MyProfile />} />
                    {/* The Lookbook route */}
                    <Route path="/look-list" element={<LookBook />} />
                  </Route>
                  <Route path="/showcase/:lookId" element={<ShowcaseLookPage />}/>
                  <Route path="/showcase-avatar/:avatarId" element={<ShowcaseAvatarPage />}/>
                  <Route path="/showcase-post" element={<ShowcasePostPage />} />
                  <Route path="/shop-the-look" element={<ShopTheLook />} />
                  <Route path="/shop-the-pin" element={<ShopThePin />} />
                  {/* <Route path="/shop-the-look" element={<ShopTheLookNew/>} /> */}
                  {/* <Route path="/shop-the-post" element={<ShopThePost/>} /> */}
                  <Route path="/shop-the-post" element={<ShopThePostNewUI />} />
                  <Route path="/browse" element={<BrowseLooksPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/remix/:lookId" element={<RemixLookPage />} />
                  {/* <Route path="/style-pin" element={<PinterestCollagePage />} /> */}
                  <Route path="/style-pin" element={<PinterestStylePinPage />} />
                  <Route path="/pinterest-remix/:pinId" element={<PinterestRemixLookPageNewUI />} />
                  <Route path="/pinterest" element={<PinterestOAuth />} />
                  </Routes>
                </Suspense>
              </Box>
            </Box>
          </ThemeProvider>
        </Router>
      </AuthProvider>
    </div>
  );
}

export default App;